/**
 * Smart section: collapsible block with summary and fill progress.
 * Shows summary when collapsed; expands on click.
 */

import { useState, type ReactNode } from "react";
import { IconChevronDown, IconCheck } from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";

export interface SmartSectionProps {
  id: string;
  title: string;
  icon: ReactNode;
  summary: string;
  filledCount: number;
  totalCount: number;
  isRequired?: boolean;
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function SmartSection({
  id,
  title,
  icon,
  summary,
  filledCount,
  totalCount,
  isRequired = false,
  hint,
  defaultOpen = false,
  children,
}: SmartSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isComplete = totalCount > 0 && filledCount >= totalCount;

  return (
    <section
      id={id}
      className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100"
    >
      <button
        type="button"
        onClick={() => {
          haptic("light");
          setIsOpen(!isOpen);
        }}
        className="w-full flex items-center gap-3 p-4 text-left active:bg-slate-50/80 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-slate-900 text-[15px]">
              {title}
            </h2>
            {isRequired && (
              <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                Обязательно
              </span>
            )}
          </div>
          <p className="text-[13px] mt-0.5 truncate text-slate-500">
            {summary}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[12px] text-slate-400 tabular-nums">
            {filledCount}/{totalCount}
          </span>
          {isComplete && (
            <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
              <IconCheck className="w-3 h-3 text-emerald-600" />
            </span>
          )}
          <IconChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      <div
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-0 border-t border-slate-100">
          {hint && (
            <p className="text-[12px] text-slate-500 mt-3 mb-2">{hint}</p>
          )}
          <div className={hint ? "pt-2" : "pt-4"}>{children}</div>
        </div>
      </div>
    </section>
  );
}
